import { useCallback, useRef, useState } from 'react'
import { Container as PixiContainer } from 'pixi.js'
import { Container, Graphics } from '@pixi/react'
import { BetHistoryButtonV2 } from './BetHistoryButtonV2'
import { CloseButtonV2 } from '../CloseButtonV2'
import { Draw } from '../../type/PixiType'
import { FeatureToggleV2Type } from '../../domain/featureToggle/enum/FeatureToggleType'
import { Ga4EventType, ga4Info } from '../../util/GA4/index'
import { LeaveGameButtonV2 } from './LeaveGameButtonV2'
import { MenuButtonV2 } from './MenuButtonV2'
import { RulesButtonV2 } from './RulesButtonV2'
import { SoundButtonV2 } from './SoundButtonV2'
import { useFeatureToggleV2 } from '../../domain/featureToggle/hooks/useFeatureToggleV2'
import { useGameRuleStore } from '../gameRule/store/gameRuleStore'

import gsap from 'gsap'
import { appScreen } from '../../util/pixi-app'

interface MenuProps {
  x: number
  y: number
  isEnabled: boolean
}

const iconSize = 120
const iconY = (260 - iconSize) / 2
const resolutionWidth = 1080
const resolutionHeight = 1920

const slidePanel = (panel: PixiContainer, targetY: number) => {
  gsap.to(panel, {
    duration: 0.2,
    y: targetY,
  })
}

export const MenuBarFeatureToggleV2 = (props: MenuProps) => {
  const [isOpened, setIsOpened] = useState(false)
  const menuPanelRef = useRef<PixiContainer>()
  const enableBackBtn = useFeatureToggleV2(FeatureToggleV2Type.enableBackBtn)
  const enableBackButtonForRedirectUrl = useFeatureToggleV2(FeatureToggleV2Type.enableBackButtonForRedirectUrl)
  const shouldShowHistory = useFeatureToggleV2(FeatureToggleV2Type.enableBetHistory)
  const shouldShowLeaveGame = enableLeaveGameButton(enableBackBtn, enableBackButtonForRedirectUrl)

  const drawBG = useCallback<Draw>((g) => {
    g.clear()
    g.beginFill('black', 1)
    g.drawRect(0, 0, resolutionWidth, 300)
    g.endFill()
  }, [])

  const drawLine = useCallback<Draw>((g) => {
    g.clear()
    g.beginFill('#B8A595', 1)
    g.drawRect(0, 0, resolutionWidth, 2)
    g.endFill()
  }, [])

  const draw = useCallback<Draw>((graphics) => {
    graphics.clear()
    graphics.beginFill('white', 0.01)
    graphics.drawRect(0, 0, resolutionWidth, resolutionHeight)
    graphics.endFill()
  }, [])

  return (
    <Container name="Menu" position={[props.x, props.y]}>
      <MenuButtonV2
        x={0}
        y={iconY - 50}
        width={iconSize}
        height={iconSize}
        onPress={() => {
          setIsOpened(true)
          slidePanel(menuPanelRef.current as PixiContainer, -50)
        }}
      />
      <Container name="Menu Panel" x={0} y={450} eventMode="static" ref={(el: PixiContainer | null) => { menuPanelRef.current = el as PixiContainer }}>
        {isOpened
          ? (
              <Container name="menu hidden bg" x={0} y={-1630} width={appScreen.width} height={appScreen.height}>
                <Graphics
                  width={appScreen.width}
                  height={appScreen.height}
                  draw={draw}
                  eventMode="static"
                  ontap={() => {
                    slidePanel(menuPanelRef.current as PixiContainer, 500)
                    setIsOpened(false)
                  }}
                  onclick={() => {
                    slidePanel(menuPanelRef.current as PixiContainer, 500)
                    setIsOpened(false)
                  }}
                />
              </Container>
            )
          : null}

        <Graphics name="menu bg" draw={drawBG} />
        <Graphics name="line" width={resolutionWidth} height={2} draw={drawLine} />

        <CloseButtonV2
          x={100}
          y={iconY}
          width={iconSize}
          height={iconSize}
          onPress={() => {
            slidePanel(menuPanelRef.current as PixiContainer, 500)
            setIsOpened(false)
          }}
        />

        <RulesButtonV2
          x={280}
          y={iconY}
          width={iconSize}
          height={iconSize}
          enabled={props.isEnabled}
          onPress={() => {
            useGameRuleStore.setState({ isOpened: true })
            ga4Info.addEvent(Ga4EventType.menu_betting_rules)
          }}
        />
        <SoundButtonV2 x={460} y={iconY} width={iconSize} height={iconSize} />

        {shouldShowHistory
          ? (
              <BetHistoryButtonV2 disabled={!props.isEnabled} x={640} y={iconY} width={iconSize} height={iconSize} />
            )
          : null}

        {shouldShowLeaveGame
          ? (
              <LeaveGameButtonV2 x={shouldShowHistory ? 820 : 640} y={iconY} width={iconSize} height={iconSize} />
            )
          : null}

      </Container>
    </Container>
  )
}

export const enableLeaveGameButton = (
  enableBackBtn: boolean,
  enableBackButtonForRedirectUrl: boolean,
): boolean => {
  if (enableBackBtn) return true
  if (enableBackButtonForRedirectUrl) {
    const url = window.sessionStorage.getItem('redirectUrl')
    return url !== null && url !== 'null'
  }
  return false
}
